import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useNavigate } from 'react-router';
import { Button } from '~/components';
import { MarketActionRow } from '~/features/shared/market-content';
import { buildB2BOpportunitiesUrl } from '~/routing';

import { b2bSideCopy } from '../../b2bSideCopy';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import { useSearchSide } from '../searchSide';
import type { Context, MsaasJobListingResponse } from '../types';

const TAGS = CT.listingSearch;

const selectEmployerUser = (state?: { context?: { employerUser?: { employerId?: number } } }) =>
  state?.context?.employerUser;

const selectListing = (state?: { context?: { listing?: MsaasJobListingResponse } }) =>
  state?.context?.listing;
const selectUncertainAttempt = (state?: { context?: Partial<Context> }) =>
  state?.context?.actionUncertain &&
  state.context.actionAttempt?.employerId === state.context.employerUser?.employerId
    ? state.context.actionAttempt
    : undefined;
const selectLoading = (state?: { context?: Partial<Context> }) => state?.context?.loading ?? false;

export const ActionRetryNotice = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const attempt = useSelector(msaasListingsSearchRef, selectUncertainAttempt);
  const loading = useSelector(msaasListingsSearchRef, selectLoading);
  return (
    <div className={attempt ? 'flex flex-wrap gap-4 items-center justify-end p-4' : undefined}>
      <p role="status" aria-live="polite">
        {attempt
          ? `We couldn't confirm whether your submission for ${attempt.listingTitle ?? `listing ${attempt.listingId}`} completed. Retry sends the original submission. Dismissing this retry does not cancel a submission already sent.`
          : ''}
      </p>
      {attempt && (
        <>
          <Button
            type="button"
            label={
              attempt.kind === 'message' ? 'Retry original message' : 'Retry original application'
            }
            disabled={loading}
            onClick={() => msaasListingsSearchRef?.send({ type: 'ON_RETRY_ACTION' })}
          />
          <Button
            type="button"
            colorVariant="tertiary"
            label="Dismiss saved retry"
            disabled={loading}
            onClick={() => msaasListingsSearchRef?.send({ type: 'ON_DISMISS_ACTION_RETRY' })}
          />
        </>
      )}
    </div>
  );
};

export const ActionButtons = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const navigate = useNavigate();
  const side = useSearchSide();
  const copy = b2bSideCopy(side);
  const employerUser = useSelector(msaasListingsSearchRef, selectEmployerUser);
  const listing = useSelector(msaasListingsSearchRef, selectListing);
  const attempt = useSelector(msaasListingsSearchRef, selectUncertainAttempt);
  const loading = useSelector(msaasListingsSearchRef, selectLoading);
  const canRetry = attempt !== undefined;

  if (!msaasListingsSearchRef) return null;

  const handleApply = () => {
    msaasListingsSearchRef.send({ type: 'ON_APPLY' });
  };

  const handleNegotiate = () => {
    msaasListingsSearchRef.send({ type: 'ON_NEGOTIATE' });
  };

  const handleMessage = () => {
    msaasListingsSearchRef.send({ type: 'ON_MESSAGE' });
  };

  const viewerResponse = listing?.jobResponses?.find(
    (r) => r.respondingEmployerId === employerUser?.employerId
  );

  // [MCV-1550] The marketplace feed is not employer-scoped, so a viewer's own
  // B2B listing turns up in their own search results. The API now refuses a
  // self-response with 403; grey the CTAs out so that refusal is never reached.
  // Both `!== undefined` legs matter: employerUser is transiently undefined
  // while the search machine loads, and `undefined === undefined` would grey
  // out every listing for that moment.
  const isOwnListing =
    listing?.employerId !== undefined &&
    employerUser?.employerId !== undefined &&
    listing.employerId === employerUser.employerId;

  const disabled = isOwnListing || loading || canRetry;
  const messageAction = {
    label: 'Message',
    colorVariant: 'tertiary' as const,
    cyTag: TAGS.detailPanel.messageBtn,
    onClick: handleMessage,
    disabled,
  };
  const actions =
    side === 'OFFERING'
      ? [
          messageAction,
          // The buyer reviews and edits the proposed terms before anything is sent, so this opens
          // the offer editor (DD17) rather than posting straight away.
          {
            label: copy.requestLaborCta,
            colorVariant: 'primary' as const,
            cyTag: 'offering-request-labor-btn',
            onClick: handleNegotiate,
            disabled,
          },
        ]
      : [
          messageAction,
          {
            label: 'Negotiate',
            colorVariant: 'secondary' as const,
            cyTag: TAGS.detailPanel.negotiateBtn,
            onClick: handleNegotiate,
            disabled,
          },
          {
            label: copy.requestLaborCta,
            colorVariant: 'primary' as const,
            cyTag: TAGS.detailPanel.applyBtn,
            onClick: handleApply,
            disabled,
          },
        ];

  return (
    // [MCV-1558] Was `fixed bottom-0 left-0 right-0`, which is positioned
    // against the viewport and so ignores the app's grid (Layout.tsx) --
    // spanning the full window width and painting over the sidebar's
    // Sign Out / Download My Data rows above min-[1360px], where the sidebar
    // sits in its own grid column. `sticky bottom-0` instead pins this bar to
    // the bottom of DetailPanel's own scroll container (see DetailPanel.tsx,
    // which renders this as a sibling of the panel rather than nested inside
    // it) so it can never drift outside that column, at any breakpoint.
    <div
      data-cy="b2b-listing-actions"
      className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-grey-900 shadow-xl z-20"
    >
      {viewerResponse ? (
        <div
          className="flex flex-wrap items-center gap-3 justify-end px-4 py-4 mx-auto text-[1rem] leading-6"
          data-cy={TAGS.detailPanel.respondedState}
        >
          <p>{copy.alreadyResponded}</p>
          <button
            onClick={() =>
              navigate(
                buildB2BOpportunitiesUrl({
                  view: 'sent',
                  responseId: viewerResponse.id,
                  // A request for someone's labor sits under Responses → Buying → Sent.
                  ...(side === 'OFFERING' && { side: 'buying' as const }),
                })
              )
            }
            className="flex items-center gap-2 cursor-pointer text-primary-50 hover:text-primary-t-300 transition-colors"
            data-cy={TAGS.detailPanel.viewResponseLink}
          >
            <ArrowTopRightOnSquareIcon className="size-5 shrink-0" />
            <span className="text-[1rem] font-semibold leading-6">View Response</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3 items-center justify-end px-4 py-4 mx-auto text-[1rem] leading-6">
          {isOwnListing && (
            <p data-cy={TAGS.detailPanel.ownListingNotice}>
              You cannot respond to your own listing.
            </p>
          )}
          <MarketActionRow actions={actions} />
        </div>
      )}
    </div>
  );
};
